import React, {
  useState,
  forwardRef,
  useRef,
  useCallback,
  useEffect,
  Fragment,
  useImperativeHandle,
} from "react"
import { useDropzone } from "react-dropzone"
import { faArrowUpFromBracket } from "@fortawesome/free-solid-svg-icons/faArrowUpFromBracket"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  StyledImageSelector,
  StyledDropzone,
  StyledDisabledPreviewImage,
  StyledUploadIcon,
} from "./index.styles"
import { FormattedMessage, useIntl } from "gatsby-plugin-intl"

import ImageCropperNew from "../ImageGallery/ImageCropperNew"
import { v4 as uuidv4 } from "uuid"
import { isSafari } from "react-device-detect"
import { useToast } from "@tmu/hooks/useToast"

const { CLOUDFLARE_IMAGE_DOMAIN = "trustmeup.com" } = process.env

const imageMaxSize = 3 * 1024 * 1024 // 3MB
const acceptedFileTypes = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
}

const isValidUrl = (url) => {
  return typeof url === "string" && url.length > 0
}

const isFileOrBlob = (value) => {
  return value instanceof File || value instanceof Blob
}

const ImageSelector = forwardRef(
  (
    {
      dashed,
      square,
      banner,
      defaultValue = "",
      onChange,
      onCropChange,
      onReset,
      disabled,
      imgWidth,
      imgHeight,
      alignOverlay,
      showUploadDescription = true,
      showUploadLabel = true,
    },
    ref
  ) => {
    const [isCropping, setIsCropping] = useState(false)
    const [currentImage, setCurrentImage] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [isPhotoHover, setIsPhotoHover] = useState(false)
    const imageRef = useRef()
    const toaster = useToast()
    const { formatMessage } = useIntl()

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      closeEdit: () => {
        setIsCropping(false)
        setCurrentImage(null)
        if (defaultValue) {
          if (isFileOrBlob(defaultValue)) {
            const objectUrl = URL.createObjectURL(defaultValue)
            setPreviewUrl(objectUrl)
          } else if (isValidUrl(defaultValue)) {
            setPreviewUrl(defaultValue)
          }
        } else {
          setPreviewUrl(null)
        }
      },
    }))

    // Handle defaultValue changes
    useEffect(() => {
      if (defaultValue) {
        if (isFileOrBlob(defaultValue)) {
          const objectUrl = URL.createObjectURL(defaultValue)
          setPreviewUrl(objectUrl)
        } else if (isValidUrl(defaultValue)) {
          setPreviewUrl(defaultValue)
        }
      } else {
        setPreviewUrl(null)
      }
    }, [defaultValue])

    const handleOnDrop = useCallback((acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        const file = acceptedFiles[0]
        setCurrentImage(file)
        // Create a preview URL for the dropped file
        const objectUrl = URL.createObjectURL(file)
        setPreviewUrl(objectUrl)
        // Don't call onChange here, wait for crop completion
        setIsCropping(true)
      }
    }, [])

    const handleDropRejected = useCallback(
      (rejectedFiles) => {
        if (rejectedFiles?.length > 0) {
          const file = rejectedFiles[0]
          if (file.errors?.some((error) => error.code === "file-too-large")) {
            toaster.error(
              formatMessage({
                id: "dashboard::forms::imageTooLarge",
                defaultMessage: "The file size must be smaller than 3MB",
              })
            )
          }
        }
      },
      [toaster, formatMessage]
    )

    const { getRootProps, getInputProps } = useDropzone({
      onDrop: handleOnDrop,
      onDropRejected: handleDropRejected,
      accept: acceptedFileTypes,
      multiple: false,
      maxSize: imageMaxSize,
      disabled: disabled || (!showUploadDescription && !showUploadLabel),
    })

    const handleCropComplete = useCallback(
      (result) => {
        if (result?.result) {
          // Create a preview URL for the cropped image
          const objectUrl = URL.createObjectURL(result.result)
          setPreviewUrl(objectUrl)
          // Call onChange with the cropped image
          onChange(result.result)
          onCropChange(result.result)
        }
        setIsCropping(false)
      },
      [onChange, onCropChange]
    )

    const handleCropCancel = useCallback(() => {
      setIsCropping(false)
      setCurrentImage(null)
      // Reset preview URL to default value
      if (defaultValue) {
        if (isFileOrBlob(defaultValue)) {
          const objectUrl = URL.createObjectURL(defaultValue)
          setPreviewUrl(objectUrl)
        } else if (isValidUrl(defaultValue)) {
          setPreviewUrl(defaultValue)
        }
      } else {
        setPreviewUrl(null)
      }
    }, [defaultValue])

    const handleReset = useCallback(
      (e) => {
        if (e) {
          e.preventDefault()
        }
        setCurrentImage(null)
        if (defaultValue) {
          if (isFileOrBlob(defaultValue)) {
            const objectUrl = URL.createObjectURL(defaultValue)
            setPreviewUrl(objectUrl)
          } else if (isValidUrl(defaultValue)) {
            setPreviewUrl(defaultValue)
          }
        } else {
          setPreviewUrl(null)
        }
        setIsCropping(false)
        onReset()
      },
      [onReset, defaultValue]
    )

    const addCdn = useCallback((src, width, height) => {
      if (!isValidUrl(src) || !width || !height) return src
      if (typeof src === "string" && src.startsWith("blob:")) return src

      let options = `width=${width},height=${height}`
      return src.includes(CLOUDFLARE_IMAGE_DOMAIN)
        ? src.replace(
            CLOUDFLARE_IMAGE_DOMAIN,
            `${CLOUDFLARE_IMAGE_DOMAIN}/cdn-cgi/image/${options},${
              !isSafari && "format=webp"
            },quality=90`
          )
        : src
    }, [])

    // Clean up object URLs when component unmounts
    useEffect(() => {
      return () => {
        if (isValidUrl(previewUrl) && previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl)
        }
      }
    }, [previewUrl])

    const imageSource = isValidUrl(previewUrl)
      ? imgWidth && imgHeight && !previewUrl.startsWith("blob:")
        ? addCdn(previewUrl, imgWidth, imgHeight)
        : previewUrl
      : null

    return (
      <>
        <StyledImageSelector className="image-selector" dashed={dashed}>
          <StyledDropzone {...getRootProps()}>
            <input {...getInputProps()} />
            {imageSource ? (
              <StyledDisabledPreviewImage
                onMouseEnter={() => setIsPhotoHover(true)}
                onMouseLeave={() => setIsPhotoHover(false)}
                square={square}>
                <img
                  ref={imageRef}
                  src={imageSource}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                />
                {isPhotoHover && (
                  <StyledUploadIcon>
                    <FontAwesomeIcon icon={faArrowUpFromBracket} />
                  </StyledUploadIcon>
                )}
              </StyledDisabledPreviewImage>
            ) : (
              <div className="upload-icon">
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
                {showUploadDescription && (
                  <FormattedMessage
                    id="dashboard::imageCropper::uploadDescription"
                    defaultMessage="Click <strong>Upload</strong> or Drag and Drop your image here"
                    values={{
                      strong: (...chunks) => (
                        <strong key="str">{chunks}</strong>
                      ),
                    }}>
                    {(...chunks) => (
                      <p>
                        {chunks.map((chunk, i) => (
                          <Fragment key={i}>{chunk}</Fragment>
                        ))}
                      </p>
                    )}
                  </FormattedMessage>
                )}
              </div>
            )}
          </StyledDropzone>
        </StyledImageSelector>

        <ImageCropperNew
          isOpen={isCropping}
          image={currentImage}
          id={`user-upload-${uuidv4()}`}
          onConfirm={handleCropComplete}
          onCancel={handleCropCancel}
          square={square}
          banner={banner}
        />
      </>
    )
  }
)

export default ImageSelector
